import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormInputBaseComponent } from '@app/shared/forms/dynamic-form/dynamic-fields/form-input-base/form-input-base.component';
import { getServerConfig } from '@app/origami-api/server-config';
import { SessionService } from '@app/session/session.service';
import { FieldNames } from '@app/origami-api/constants/field-names';
import { TopicUtils } from '@app/origami-api/utils/topic-utils';
import { eSignatureVendor } from '@app/origami-api/constants/e-signature-vendor';
import { RoomTypes } from '@app/origami-api/constants/room-types';
import { TiFeatures } from '@app/origami-api/constants/features';
import { SponsorService } from '@app/origami-api/services/sponsor.service';
import { Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { TopicSearchService } from '@app/origami-api/services/topic-search.service';
import { TopicTypeId } from '@app/origami-api/constants/topic-type-names';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { DialogService } from '@app/services';
import { TopicService } from '@app/origami-api/services/topic.service';
import { AbstractControl } from '@angular/forms';
import Site = FieldNames.Site;
import { ServiceOffering } from '@app/origami-api/models/room-type';
import { isEventManagementSupportedByServiceOffering } from '@app/origami-api/utils/service-offering.utils';
import { RadioGroupItem } from '@tpt/common/inputs/radio-group';

interface IGeneralRoomOptions {
  LMSValue: boolean;
  CTMSValue: boolean;
  doNotGenerateDefaultFolder: boolean;
  eSignatureOn: boolean;
  eSignatureTypeId: eSignatureVendor;
  useForCloning: boolean;
  cloningCategoryId: number;
  enableCloneInvSites: boolean;
  cloneInvSitesFromSiteId: number;
  retainInvSiteStatus: null;
  enableCommunication: boolean;
  enableEventManagement: boolean;
  outboxValue: boolean;
}

export enum ServiceOfferingTypes {
  CCR = 'CCR',
  eISF = 'eISF',
  RMR = 'RMR',
  CWS = 'CWS',
  QMS = 'QMS',
  QMS2 = 'QMS2',
  QDOC = 'QDOC',
  DMS = 'DMS',
  RDOC = 'RDOC',
  SAFE = 'SAFE',
  CSV = 'CSV',
  ADJ = 'ADJ',
  TPT = 'TPT',
  ARCH = 'ARCH',
  eTMF = 'eTMF',
  SSU = 'SSU',
}

@Component({
  standalone: false,
  selector: 'ti-form-general-room-options',
  styleUrls: ['./room-form-general-room-options.component.sass'],
  templateUrl: './room-form-general-room-options.component.html',
})
export class RoomFormGeneralRoomOptionsComponent
  extends FormInputBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('sourceRoomDropdown') sourceRoomDropdown: DropDownListComponent;

  readonly strings = tiStrings.tiComponents.room.components.roomProfileWizard;
  readonly miscStrings = tiStrings.app.misc;
  readonly esignStrings = tiStrings.eSign.common;
  private requiredFields: any = {}; //
  readonly retainStatusString = 'retainStatus';
  readonly retainStatusOptions: RadioGroupItem<boolean>[] = [
    { title: this.strings.retainTheStatus, value: true },
    { title: this.strings.pending, value: false },
  ];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private sessionService: SessionService,
    private sponsorService: SponsorService,
    private topicSearchService: TopicSearchService,
    private dialogService: DialogService,
    private topicService: TopicService,
  ) {
    super();
  }

  public esignaturesData: { id: eSignatureVendor; name: string }[] = [];
  public cloningCategoryData: { topicId: number; topicName: string }[] = [];
  public cloningCategoryLoading = false;
  public sourceRoomData: { siteId: number; topicName: string }[] = [];
  public sourceRoomAll: { siteId: number; topicName: string }[] = [];
  public serviceOfferings: ServiceOffering[] = [];

  private eSignatureVendorADOBE: string = this.esignStrings.adobeSignEsignType;
  private eSignatureVendorDOCU: string = this.esignStrings.docuSignEsignType;
  private eSignatureVendorTI: string = this.esignStrings.tiESignatureEsignType;
  private eSignatureVendorMSB: string = this.esignStrings.msbESignatureEsignType;

  public isLMSActive: boolean;
  public isCTMSActive: boolean;
  public notSpecifiedCategory = { topicId: null, topicName: this.miscStrings.notSpecified };
  public esignatureDisabled: boolean = true;
  isMultitenantInstance: boolean;
  isEventActive: boolean;

  public setValueObj: IGeneralRoomOptions = {
    LMSValue: false,
    CTMSValue: false,
    doNotGenerateDefaultFolder: false,
    eSignatureOn: false,
    eSignatureTypeId: null,
    useForCloning: false,
    cloningCategoryId: this.notSpecifiedCategory.topicId,
    enableCloneInvSites: false,
    cloneInvSitesFromSiteId: null,
    retainInvSiteStatus: null,
    enableEventManagement: false,
    enableCommunication: false,
    outboxValue: false,
  };

  sponsorId: number | null = null;
  cloneInvSitesAvailable: boolean;
  editMode: boolean;
  sourceRoomsLoading: boolean;

  ngOnInit() {
    super.ngOnInit();
    this.getServicOfferings();
    this.isMultitenantInstance = getServerConfig().isMultitenantInstance;
    this.editMode = this.config.UIData.editMode;

    if (this.self.value) {
      this.setValueObj = this.self.value;
    } else {
      this.updateFormValue();
    }

    this.self.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (!val) {
        this.self.setValue(<IGeneralRoomOptions>{});
        return;
      }
      Object.assign(this.setValueObj, val);
    });
    this.checkControlAvailability();
    this.subscribeToSponsorChanges();
    this.subscribeToServiceOfferingChanges();
    //this.setCloningCategoryData();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.setCloningCategoryData();
  }

  private checkControlAvailability() {
    const roomTypeId = +this.formGroup.get(FieldNames.Site.RoomTypeId).value;

    this.isLMSActive = getServerConfig().serverCfg.disabledFeatures.indexOf(TiFeatures.LMSIntegration) === -1;

    this.isCTMSActive =
      getServerConfig().serverCfg.disabledFeatures.indexOf(TiFeatures.CtmsIntegration) === -1 &&
      [RoomTypes.eTMF, RoomTypes.Collaborate, RoomTypes.SSU].indexOf(roomTypeId) !== -1;
  }

  private checkEventsAvailability() {
    const roomTypeId = +this.formGroup.get(FieldNames.Site.RoomTypeId).value;
    const serviceOfferingId = this.formGroup.get(FieldNames.Site.ServiceOffering).value;
    const offering = this.serviceOfferings.find((serviceOffering) => serviceOffering.id === serviceOfferingId)
      ?.ShortName;

    this.isEventActive =
      getServerConfig().serverCfg.disabledFeatures.indexOf(TiFeatures.EventManagement) === -1 &&
      isEventManagementSupportedByServiceOffering(offering) &&
      roomTypeId !== RoomTypes.General;
  }

  private clearEventManagementIfUnavailable(): void {
    this.checkEventsAvailability();
    if (!this.isEventActive && this.setValueObj.enableEventManagement) {
      this.setValueObj.enableEventManagement = false;
      this.updateFormValue();
    }
  }

  private subscribeToServiceOfferingChanges(): void {
    const serviceOfferingControl = this.formGroup.get(FieldNames.Site.ServiceOffering);
    const roomTypeControl = this.formGroup.get(FieldNames.Site.RoomTypeId);

    serviceOfferingControl?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(() => this.clearEventManagementIfUnavailable());

    roomTypeControl?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(() => this.clearEventManagementIfUnavailable());
  }

  private subscribeToSponsorChanges(): void {
    const sponsorControl = this.formGroup.get(FieldNames.Site.SponsorId);

    if (!sponsorControl) {
      this.getPossibleEsignatures();
      return;
    }

    const applySponsorId = (rawValue: unknown) => {
      const nextSponsorId = +rawValue > 0 ? +rawValue : null;
      const sponsorChanged =
        this.sponsorId !== null && this.sponsorId !== undefined && this.sponsorId !== nextSponsorId;

      this.sponsorId = nextSponsorId;
      this.cloneInvSitesAvailable = !!this.sponsorId && !this.editMode;

      if (sponsorChanged) {
        this.setValueObj.eSignatureOn = false;
        this.setValueObj.eSignatureTypeId = null;
        this.updateFormValue();
      }

      this.getPossibleEsignatures();

      if (this.cloneInvSitesAvailable) {
        this.setInvestigativeSourceRoomData();
      } else {
        this.sourceRoomData = [];
        this.sourceRoomAll = [];
      }
    };

    applySponsorId(sponsorControl.value);
    sponsorControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(applySponsorId);
  }

  getPossibleEsignatures() {
    const studySponsorId = this.sponsorId;
    if (this.isMultitenantInstance && studySponsorId) {
      this.sponsorService
        .getSponsor(+studySponsorId)
        //.pipe(takeUntil(this.unsubscribe$)) // with this approach sometimes getSponsor "stucks" in a running state, so latest calls will return no info at all, so let's fall back to the take(1)
        .pipe(take(1))
        .subscribe((res) => {
          if (!res.Success) {
            this.disableEsignatures();
            return;
          }

          const sponsorEsignaturesString = TopicUtils.getTopicFieldValue(
            res.Result,
            FieldNames.Sponsor.UseESignature,
            true,
          );

          if (!sponsorEsignaturesString) {
            this.disableEsignatures();
            return;
          }

          const sponsorEsignatures = sponsorEsignaturesString.split(',').map((v) => +v);
          this.setESignaturesData(sponsorEsignatures);
        });
    } else if (!this.isMultitenantInstance) {
      const settingsEsignatures = this.sessionService.settings.security.esignature.availableProviders.get();
      if (settingsEsignatures?.length) {
        this.setESignaturesData(settingsEsignatures);
      } else {
        this.disableEsignatures();
      }
    } else {
      this.disableEsignatures();
    }
  }

  private disableEsignatures(): void {
    this.esignatureDisabled = true;
    this.esignaturesData = [];
  }

  private setCloningCategoryData() {
    this.cloningCategoryLoading = true;
    this.topicSearchService
      .searchTopics({
        siteId: this.sessionService.domain.id,
        topicTypeId: TopicTypeId.SiteCloningCategory,
      })
      .subscribe((res) => {
        const topicList = res.Result.TopicList;
        const categories = topicList
          .map((topic) => ({ topicName: topic.TopicName, topicId: topic.TopicId }))
          .sort((category1, category2) =>
            category1.topicName.toLowerCase() > category2.topicName.toLowerCase() ? 1 : -1,
          );
        categories.unshift(this.notSpecifiedCategory);
        this.cloningCategoryData = categories;
        this.cloningCategoryLoading = false;
      });
  }

  private setESignaturesData(possibleEsignatures: number[]) {
    const usableProviders = (possibleEsignatures || [])
      .map((id) => +id)
      .filter((id) => !Number.isNaN(id) && id !== eSignatureVendor.none);

    if (!usableProviders.length) {
      this.disableEsignatures();
      return;
    }

    this.esignatureDisabled = false;
    const esignaturesData = [];
    usableProviders.forEach((eSignatureId) => {
      switch (eSignatureId) {
        case eSignatureVendor.adobe:
          esignaturesData.push({
            id: eSignatureVendor.adobe,
            name: this.eSignatureVendorADOBE,
          });
          break;
        case eSignatureVendor.docu:
          esignaturesData.push({
            id: eSignatureVendor.docu,
            name: this.eSignatureVendorDOCU,
          });
          break;
        case eSignatureVendor.ti:
          esignaturesData.push({
            id: eSignatureVendor.ti,
            name: this.eSignatureVendorTI,
          });
          break;
        case eSignatureVendor.msb:
          esignaturesData.push({
            id: eSignatureVendor.msb,
            name: this.eSignatureVendorMSB,
          });
          break;
      }
    });

    if (!esignaturesData.length) {
      this.disableEsignatures();
      return;
    }

    this.esignaturesData = esignaturesData;

    const roomTypeId = this.formGroup.value[Site.RoomTypeId];
    const isCWSRoom = roomTypeId === RoomTypes.Collaborate;
    if (isCWSRoom && !this.editMode) {
      if (!this.self.value || !this.self.value.eSignatureOn) {
        this.self.setValue({ eSignatureOn: true });
        this.setDefaultEsignature();
      }
    }
  }

  private setInvestigativeSourceRoomData() {
    if (this.cloneInvSitesAvailable) {
      this.sourceRoomsLoading = true;
      this.topicSearchService
        .searchTopics(
          {
            siteId: this.sessionService.domain.id,
            topicTypeId: TopicTypeId.Site,
            searchQuery: `${FieldNames.Site.EnableEtmf} = 1 AND ${FieldNames.Site.SponsorId} = ${this.sponsorId}`,
          },
          false,
        )
        .subscribe(
          (res) => {
            const topicList = res.Result.TopicList;
            this.sourceRoomAll = topicList
              .map((topic) => ({
                topicName: topic.TopicName,
                siteId: TopicUtils.getTopicFieldValue(topic.TopicFields, FieldNames.Site.SiteId),
              }))
              .sort((category1, category2) =>
                category1.topicName.toLowerCase() > category2.topicName.toLowerCase() ? 1 : -1,
              );
            this.sourceRoomData = this.sourceRoomAll.slice();
            this.sourceRoomsLoading = false;
          },
          (err) => {
            this.sourceRoomsLoading = false;
          },
        );
    }
  }

  public LMSChange(value: boolean) {
    this.setValueObj.LMSValue = value;
    this.updateFormValue();
  }

  public communicationChange(value: boolean) {
    this.setValueObj.enableCommunication = value;
    this.updateFormValue();
  }

  public eventChange(value: boolean) {
    this.setValueObj.enableEventManagement = value;
    this.updateFormValue();
  }

  public outboxChange(value: boolean) {
    this.setValueObj.outboxValue = value;
    this.updateFormValue();
  }

  public CTMSChange(value: boolean) {
    this.setValueObj.CTMSValue = value;
    this.updateFormValue();
  }

  public doNotGenerateDefaultFolderChange(value: boolean) {
    this.setValueObj.doNotGenerateDefaultFolder = value;
    this.updateFormValue();
  }

  public useForCloningChange(value: boolean) {
    this.setValueObj.useForCloning = value;
    if (!this.setValueObj.useForCloning) {
      this.cloningCategoryChange(this.notSpecifiedCategory);
    }
    this.updateFormValue();
  }

  public eSignatureChange(value: boolean) {
    this.setValueObj.eSignatureOn = value;
    this.updateFormValue();
    this.setDefaultEsignature();
  }

  public setDefaultEsignature() {
    if (this.setValueObj.eSignatureOn && !this.setValueObj.eSignatureTypeId) {
      const defaultEsignature =
        this.esignaturesData.find((eSignatureType) => eSignatureType.id === eSignatureVendor.ti) ||
        this.esignaturesData[0];
      this.eSignatureValueChange(defaultEsignature);
    }
  }

  public eSignatureValueChange(value: { id: eSignatureVendor; name: string }) {
    this.setValueObj.eSignatureTypeId = value.id;
    this.updateFormValue();
  }

  public cloningCategoryChange(value: { topicId: number; topicName: string }) {
    this.setValueObj.cloningCategoryId = value.topicId;
    this.updateFormValue();
  }

  public updateFormValue() {
    this.self.setValue(this.setValueObj);
  }

  public addNewCategory() {
    this.dialogService
      .askText({
        title: this.strings.newCloningCategory,
        inputType: 'input',
        label: this.strings.cloningCategory,
        required: true,
        btnOkTitle: this.miscStrings.create,
        btnCancelTitle: this.miscStrings.cancel,
      })
      .then((res) => {
        if (res.action === 'yes') {
          this.cloningCategoryLoading = true;
          this.topicService
            .createTopic({
              siteId: this.sessionService.domain.id,
              topic: {
                TopicName: res.text,
                TopicTypeId: TopicTypeId.SiteCloningCategory,
              },
            })
            .subscribe(
              (response) => {
                this.cloningCategoryLoading = false;
                if (response.Success) {
                  const newCategory = { topicName: res.text, topicId: response.Result.Id };
                  this.cloningCategoryData.push(newCategory);
                  this.cloningCategoryChange(newCategory);
                }
              },
              (err) => {
                this.cloningCategoryLoading = false;
              },
            );
        }
      });
  }

  setFieldRequired(fieldName) {
    this.requiredFields[fieldName] = true;
  }

  unsetFieldRequired(fieldName) {
    this.requiredFields[fieldName] = false;
  }

  enableCloneInvSitesChange(value: boolean) {
    this.setValueObj.enableCloneInvSites = value;
    const validators = [];
    this.setValueObj.retainInvSiteStatus = null;
    if (!this.setValueObj.enableCloneInvSites) {
      this.unsetFieldRequired(this.retainStatusString);
    } else {
      this.setFieldRequired(this.retainStatusString);
    }

    if (this.requiredFields[this.retainStatusString]) {
      validators.push(this.isRetainInvSiteStatusRequired());
    }

    this.self.clearValidators();
    this.self.setValidators(validators);

    this.updateFormValue();
  }

  changeRetainInvSiteStatus() {
    this.updateFormValue();
  }

  private isRetainInvSiteStatusRequired() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isOk =
        control.value && control.value.retainInvSiteStatus !== null && control.value.retainInvSiteStatus !== undefined;
      return isOk ? null : { statusRequired: { value: control.value } };
    };
  }

  public sourceRoomChange(value: { siteId: number; topicName: string }) {
    this.setValueObj.cloneInvSitesFromSiteId = value.siteId;
    this.updateFormValue();
  }

  filterSourceRooms(searchString: string) {
    this.sourceRoomData = this.sourceRoomAll.filter((topic) => topic.topicName.toLowerCase().includes(searchString));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public getStatusError() {
    const field = this.formGroup.get(this.config.fieldName);
    if (field.hasError('statusRequired')) {
      return this.strings.thisFieldIsRequired;
    }

    return null;
  }

  public getServicOfferings(): void {
    this.topicSearchService
      .searchTopics({ siteId: this.sessionService.currentRoomId, topicTypeId: TopicTypeId.RoomServiceOffering })
      .pipe(take(1))
      .subscribe((response) => {
        this.serviceOfferings = (response.Result.TopicList || []).map((topic) => {
          return {
            id: topic.TopicId,
            LongName: topic.TopicName,
            ShortName: TopicUtils.getTopicFieldValue(topic.TopicFields, FieldNames.ServiceOffering.Offering),
          };
        });
        this.clearEventManagementIfUnavailable();
      });
  }
}
